/*
 * Autoplays the videos on the product page with their audio on.
 *
 * The gallery already loads and plays whichever media the shopper is on, so
 * this hooks that moment and adds the sound the theme leaves off, plus the two
 * cases the theme does not cover: a video that is the first media of a product,
 * and a video the shopper swipes to on mobile.
 *
 * Browsers refuse an unmuted autoplay until the visitor has interacted with the
 * page - Chrome answers by pausing the video the instant it is unmuted, so
 * every attempt is checked afterwards rather than trusted. A refused video
 * keeps playing muted and is unmuted on the first click, tap or key press.
 */
(function () {
  const awaitingGesture = new Set(); // media refused audio, waiting for a gesture
  let listeningForGesture = false;
  let pauseTimer = null;

  function sendCommand(iframe, message) {
    const post = () => iframe.contentWindow && iframe.contentWindow.postMessage(message, '*');
    post();
    iframe.addEventListener('load', post, { once: true });
  }

  // An element that is not rendered has no client rects, which covers both the
  // slides the layout hides and the video the mobile CSS keeps hidden until its
  // slide is the active one.
  function isRendered(element) {
    return element.isConnected && element.getClientRects().length > 0;
  }

  function listenForGesture() {
    if (listeningForGesture) return;
    listeningForGesture = true;

    const events = ['pointerdown', 'touchstart', 'keydown'];
    const onGesture = () => {
      events.forEach((name) => document.removeEventListener(name, onGesture, true));
      listeningForGesture = false;
      const pending = Array.from(awaitingGesture);
      awaitingGesture.clear();
      pending.forEach((element) => {
        if (isRendered(element)) playWithAudio(element);
      });
    };

    events.forEach((name) => document.addEventListener(name, onGesture, true));
  }

  function playMuted(video) {
    video.muted = true;
    video.play().catch(() => {});
    awaitingGesture.add(video);
    listenForGesture();
  }

  function playWithAudio(element) {
    if (element.nodeName === 'VIDEO') {
      element.muted = false;
      element.volume = 1;

      // Chrome resolves the play() and then pauses the video instead of
      // rejecting it, so confirm it really is playing out loud a moment later.
      const confirmPlaying = () => {
        if (element.paused || element.muted) playMuted(element);
      };
      element.play().then(() => setTimeout(confirmPlaying, 300), () => playMuted(element));
      return;
    }

    if (element.classList.contains('js-youtube')) {
      sendCommand(element, '{"event":"command","func":"unMute","args":""}');
      sendCommand(element, '{"event":"command","func":"playVideo","args":""}');
    } else if (element.classList.contains('js-vimeo')) {
      sendCommand(element, '{"method":"setVolume","value":1}');
      sendCommand(element, '{"method":"play"}');
    } else {
      return;
    }

    // YouTube and Vimeo silently ignore an autoplay they are not allowed to
    // start, so there is nothing to catch - retry after the next interaction.
    awaitingGesture.add(element);
    listenForGesture();
  }

  function isProductMedia(element) {
    return !!element.closest('.product-media-container, .product-media-modal__content');
  }

  // Every video reaches the page the same way: the gallery clones it out of the
  // deferred-media template and appends it.
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE || !isProductMedia(node)) return;
        if (node.nodeName === 'VIDEO' || node.nodeName === 'IFRAME') playWithAudio(node);
        else node.querySelectorAll('video, iframe').forEach(playWithAudio);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  function mediaOf(slide) {
    const media = slide && slide.querySelector('deferred-media');
    return media && isRendered(media) ? media : null;
  }

  function startSlide(slide) {
    const media = mediaOf(slide);
    if (!media) return;
    if (!media.getAttribute('loaded') && typeof media.loadContent === 'function') {
      media.loadContent(false); // the observer above gives it audio
      return;
    }
    const video = media.querySelector('video, iframe');
    if (video) playWithAudio(video);
  }

  document.querySelectorAll('media-gallery').forEach((gallery) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');

    // A video that is the product's first media: the theme shows its poster and
    // waits for a click, so start it here.
    startSlide(gallery.querySelector('.product__media-item.is-active'));

    // Swiping the mobile slider only moves the thumbnail highlight, so hand the
    // slide to the gallery the way a thumbnail tap would and let it play.
    if (viewer) {
      viewer.addEventListener('slideChanged', (event) => {
        const slide = event.detail.currentElement;
        if (!slide || slide.classList.contains('is-active') || !slide.querySelector('deferred-media')) return;
        if (typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(slide.dataset.mediaId, false);
      });
    }

    // Nothing should keep talking once the gallery is off screen, and it picks
    // back up when the shopper scrolls to it again.
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          clearTimeout(pauseTimer);
          if (entry.isIntersecting) {
            startSlide(gallery.querySelector('.product__media-item.is-active'));
          } else {
            // Selecting a slide scrolls the gallery, so let that settle first.
            pauseTimer = setTimeout(() => {
              if (!entry.target.getClientRects().length || !isInViewport(entry.target)) {
                gallery.querySelectorAll('video').forEach((video) => video.pause());
                gallery.querySelectorAll('iframe.js-youtube').forEach((f) => sendCommand(f, '{"event":"command","func":"pauseVideo","args":""}'));
                gallery.querySelectorAll('iframe.js-vimeo').forEach((f) => sendCommand(f, '{"method":"pause"}'));
              }
            }, 600);
          }
        });
      },
      { threshold: 0 }
    ).observe(gallery);
  });

  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
  }
})();
